interface AddendumCreationErrorInput {
  error: unknown;
}

interface ApolloErrorLike {
  graphQLErrors?: ReadonlyArray<{ message?: unknown }>;
  message?: unknown;
  networkError?: unknown;
}

type KnownAddendumCreationErrorKind =
  | 'reportNotSigned'
  | 'reportNotSent'
  | 'addendumAlreadyOpen'
  | 'caseLocked'
  | 'invalidCaseStatus'
  | 'claimConflict'
  | 'network';

export type AddendumCreationErrorDisplay =
  | {
      kind: KnownAddendumCreationErrorKind;
      reason: string;
    }
  | {
      kind: 'generic';
    };

export function getAddendumCreationErrorDisplay({
  error,
}: AddendumCreationErrorInput): AddendumCreationErrorDisplay {
  if (error == null || typeof error !== 'object') {
    return { kind: 'generic' };
  }

  const apolloError = error as ApolloErrorLike;
  const graphQLErrorMessage = (
    Array.isArray(apolloError.graphQLErrors) ? apolloError.graphQLErrors : []
  ).find(({ message }) => typeof message === 'string' && message.trim() !== '')?.message;

  const message =
    typeof graphQLErrorMessage === 'string'
      ? graphQLErrorMessage
      : typeof apolloError.message === 'string'
        ? apolloError.message
        : '';

  const normalizedMessage = message.trim().replace(/\.$/, '');

  if (normalizedMessage === "Can't create addendum from an unsubmitted report") {
    return {
      kind: 'reportNotSigned',
      reason: 'the report not being signed',
    };
  }

  if (
    normalizedMessage ===
    "Can't create addendum, the report has been submitted but has not been sent to the clinic yet"
  ) {
    return {
      kind: 'reportNotSent',
      reason: 'the signed report not yet being sent to the clinic',
    };
  }

  if (
    normalizedMessage === "Can't create addendum because another addendum is open for this case"
  ) {
    return {
      kind: 'addendumAlreadyOpen',
      reason: 'another addendum already being open for this case',
    };
  }

  const lockedByEmail = normalizedMessage.match(/^locked_by\s*[:-]\s*([^\s@]+@[^\s@]+)$/)?.[1];

  if (lockedByEmail) {
    return {
      kind: 'caseLocked',
      reason: `the case being locked by ${lockedByEmail}`,
    };
  }

  if (/^status\s*[:-]\s*\S/.test(normalizedMessage)) {
    return {
      kind: 'invalidCaseStatus',
      reason: "the case's current status not allowing an addendum",
    };
  }

  if (/^claimed\s*[:-]\s*False$/i.test(normalizedMessage)) {
    return {
      kind: 'claimConflict',
      reason: 'the case changing while it was being claimed',
    };
  }

  if (apolloError.networkError) {
    return {
      kind: 'network',
      reason: 'a connection error',
    };
  }

  return { kind: 'generic' };
}
